/**
 * Shared Pricing Engine
 *
 * This service provides unified pricing calculations for both:
 * - Simple per-option pricing (ProductOptionModal)
 * - Complex configurator-level pricing (CPQConfigurator)
 *
 * Supports:
 * - Per unit pricing: basePrice + (value × pricePerUnit)
 * - Fixed pricing: Always same price regardless of value
 * - Formula-based pricing: Custom calculations with {value} variable
 * - Matrix pricing: value × otherValue × pricePerUnit
 * - Tiered pricing (staffels): Volume discounts based on total
 * - Bundle pricing: Discounts when multiple options selected
 *
 * @example
 * // Basic usage
 * const engine = new PricingEngine();
 * engine.registerOption('breedte', { pricingType: 'per_unit', pricePerUnit: 2 });
 * const price = engine.calculateOptionPrice('breedte', 150); // €300
 *
 * @example
 * // Matrix pricing
 * engine.registerOption('hoogte', {
 *   pricingType: 'matrix',
 *   matrixConfig: { otherField: 'breedte', pricePerUnit: 15 }
 * });
 * const price = engine.calculateOptionPrice('hoogte', 200, { breedte: 150 }); // €45.000
 *
 * @example
 * // Cross-option rules
 * engine.addRule({
 *   type: 'tiered',
 *   conditions: [{ type: 'total_min', value: 1000 }],
 *   pricing: { percentage: 5 }
 * });
 * const total = engine.calculateTotal({ breedte: 150, hoogte: 200 }); // With 5% discount
 */

export class PricingEngine {
  constructor() {
    this.rules = [];
    this.options = new Map();
    this.values = new Map();
  }

  /**
   * Register a product option with its pricing configuration
   */
  registerOption(optionId, config) {
    this.options.set(optionId, config);
  }

  /**
   * Set current form values
   */
  setValues(values) {
    this.values = new Map(Object.entries(values));
  }

  /**
   * Calculate price for a single option
   */
  calculateOptionPrice(optionId, value, otherValues = {}) {
    const config = this.options.get(optionId);
    if (!config) return 0;

    switch (config.pricingType) {
      case 'per_unit':
        return this.calculatePerUnit(config, value);

      case 'fixed':
        return this.calculateFixed(config);

      case 'formula':
        return this.calculateFormula(config, value, otherValues);

      case 'matrix':
        return this.calculateMatrix(config, value, otherValues);

      default:
        return 0;
    }
  }

  /**
   * Calculate total price for all options
   */
  calculateTotal(values = {}) {
    let total = 0;

    // Set current values
    this.setValues(values);

    // Calculate each option
    for (const [optionId, value] of this.values) {
      if (value !== null && value !== undefined && value !== '') {
        total += this.calculateOptionPrice(optionId, value, values);
      }
    }

    // Apply configurator-level rules (staffels, bundles, etc.)
    total = this.applyConfiguratorRules(total, values);

    return Math.max(0, total);
  }

  /**
   * Per unit pricing: basePrice + (value * pricePerUnit)
   */
  calculatePerUnit(config, value) {
    const basePrice = parseFloat(config.basePrice) || 0;
    const pricePerUnit = parseFloat(config.pricePerUnit) || 0;
    const numericValue = parseFloat(value) || 0;

    return basePrice + numericValue * pricePerUnit;
  }

  /**
   * Fixed pricing: always returns the same price
   */
  calculateFixed(config) {
    return parseFloat(config.fixedPrice) || 0;
  }

  /**
   * Formula-based pricing with safe evaluation
   */
  calculateFormula(config, value, otherValues = {}) {
    if (!config.formula) return 0;

    try {
      let expression = config.formula;

      // Replace main value
      expression = expression.replace(/\{value\}/g, (parseFloat(value) || 0).toString());

      // Replace other values if referenced
      for (const [key, val] of Object.entries(otherValues)) {
        const regex = new RegExp(`\\{${key}\\}`, 'g');
        expression = expression.replace(regex, (parseFloat(val) || 0).toString());
      }

      // Safety check: only allow numbers, operators, and parentheses
      if (!/^[0-9+\-*/.() ]+$/.test(expression)) {
        console.warn('Invalid formula expression:', expression);
        return 0;
      }

      // Use Function constructor instead of eval for better security
      const result = Function(`'use strict'; return (${expression})`)();
      return isNaN(result) ? 0 : parseFloat(result);
    } catch (error) {
      console.warn('Formula calculation error:', error);
      return 0;
    }
  }

  /**
   * Matrix pricing: value × otherValue × pricePerUnit
   */
  calculateMatrix(config, value, otherValues = {}) {
    const matrixConfig = config.matrixConfig || {};
    const otherField = matrixConfig.otherField;

    if (!otherField || !otherValues[otherField]) {
      return 0;
    }

    // Parse values - handle both direct numbers and choice values
    const firstValue = this.parseNumericValue(value);
    const secondValue = this.parseNumericValue(otherValues[otherField]);
    const pricePerUnit = parseFloat(matrixConfig.pricePerUnit) || 0;
    const minimumPrice = parseFloat(matrixConfig.minimumPrice) || 0;

    // Simple multiplication: firstValue × secondValue × pricePerUnit
    const price = firstValue * secondValue * pricePerUnit;
    const finalPrice = Math.max(price, minimumPrice);

    return finalPrice;
  }

  /**
   * Parse a value to number - handles choice values, direct numbers, etc.
   * For choices, this will receive the choice ID, but we need to parse the label
   */
  parseNumericValue(value) {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    // If it's already a number, return it
    if (typeof value === 'number') {
      return value;
    }

    // If it's a string, try to parse it as a number
    // This works for both direct numeric strings and choice labels like "150cm"
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Apply configurator-level rules (staffels, bundles, etc.)
   */
  applyConfiguratorRules(baseTotal, values) {
    let total = baseTotal;

    // Apply each rule in order
    for (const rule of this.rules) {
      if (this.evaluateRuleConditions(rule, values, total)) {
        total = this.applyRule(rule, total, values);
      }
    }

    return total;
  }

  /**
   * Add a configurator-level pricing rule
   */
  addRule(rule) {
    this.rules.push({
      id: rule.id || `rule_${Date.now()}`,
      type: rule.type, // 'tiered', 'bundle', 'discount', etc.
      conditions: rule.conditions || [],
      pricing: rule.pricing || {},
      priority: rule.priority || 0,
      ...rule,
    });

    // Sort by priority (higher priority first)
    this.rules.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }

  /**
   * Remove a pricing rule
   */
  removeRule(ruleId) {
    this.rules = this.rules.filter((rule) => rule.id !== ruleId);
  }

  /**
   * Clear all rules
   */
  clearRules() {
    this.rules = [];
  }

  /**
   * Evaluate if rule conditions are met
   */
  evaluateRuleConditions(rule, values, currentTotal) {
    if (!rule.conditions || rule.conditions.length === 0) {
      return true;
    }

    return rule.conditions.every((condition) => {
      switch (condition.type) {
        case 'total_min':
          return currentTotal >= (parseFloat(condition.value) || 0);

        case 'total_max':
          return currentTotal <= (parseFloat(condition.value) || 0);

        case 'option_equals':
          return values[condition.optionId] == condition.value;

        case 'option_min':
          return parseFloat(values[condition.optionId] || 0) >= (parseFloat(condition.value) || 0);

        case 'option_max':
          return parseFloat(values[condition.optionId] || 0) <= (parseFloat(condition.value) || 0);

        case 'options_selected':
          return condition.optionIds.every(
            (id) => values[id] !== null && values[id] !== undefined && values[id] !== ''
          );

        default:
          return true;
      }
    });
  }

  /**
   * Apply a specific rule to the total
   */
  applyRule(rule, currentTotal, values) {
    switch (rule.type) {
      case 'tiered':
        return this.applyTieredPricing(rule, currentTotal, values);

      case 'bundle':
        return this.applyBundleDiscount(rule, currentTotal, values);

      case 'percentage_discount':
        return currentTotal * (1 - (parseFloat(rule.pricing.percentage) || 0) / 100);

      case 'fixed_discount':
        return Math.max(0, currentTotal - (parseFloat(rule.pricing.amount) || 0));

      case 'fixed_price':
        return parseFloat(rule.pricing.amount) || 0;

      default:
        return currentTotal;
    }
  }

  /**
   * Apply tiered pricing (staffels)
   */
  applyTieredPricing(rule, currentTotal, values) {
    const tiers = rule.pricing.tiers || [];

    for (const tier of tiers.sort((a, b) => (b.minValue || 0) - (a.minValue || 0))) {
      if (currentTotal >= (parseFloat(tier.minValue) || 0)) {
        switch (tier.type) {
          case 'percentage':
            return currentTotal * (1 - (parseFloat(tier.discount) || 0) / 100);

          case 'fixed_amount':
            return Math.max(0, currentTotal - (parseFloat(tier.discount) || 0));

          case 'price_override':
            return parseFloat(tier.price) || 0;
        }
        break;
      }
    }

    return currentTotal;
  }

  /**
   * Apply bundle discount
   */
  applyBundleDiscount(rule, currentTotal, values) {
    const requiredOptions =
      rule.conditions.find((c) => c.type === 'options_selected')?.optionIds || [];

    // Check if all required options are selected
    const allSelected = requiredOptions.every(
      (id) => values[id] !== null && values[id] !== undefined && values[id] !== ''
    );

    if (allSelected) {
      const discount = rule.pricing.discount || 0;
      const type = rule.pricing.type || 'percentage';

      switch (type) {
        case 'percentage':
          return currentTotal * (1 - discount / 100);

        case 'fixed_amount':
          return Math.max(0, currentTotal - discount);

        default:
          return currentTotal;
      }
    }

    return currentTotal;
  }

  /**
   * Get detailed price breakdown
   */
  getBreakdown(values = {}) {
    const breakdown = {
      options: [],
      subtotal: 0,
      rules: [],
      total: 0,
    };

    this.setValues(values);

    // Calculate each option
    for (const [optionId, value] of this.values) {
      if (value !== null && value !== undefined && value !== '') {
        const price = this.calculateOptionPrice(optionId, value, values);
        const config = this.options.get(optionId);

        breakdown.options.push({
          optionId,
          value,
          price,
          config,
          calculation: this.getCalculationDescription(config, value, values),
        });

        breakdown.subtotal += price;
      }
    }

    // Apply rules
    let total = breakdown.subtotal;
    for (const rule of this.rules) {
      if (this.evaluateRuleConditions(rule, values, total)) {
        const originalTotal = total;
        total = this.applyRule(rule, total, values);

        breakdown.rules.push({
          rule,
          originalTotal,
          newTotal: total,
          discount: originalTotal - total,
        });
      }
    }

    breakdown.total = total;
    return breakdown;
  }

  /**
   * Get human-readable calculation description
   */
  getCalculationDescription(config, value, otherValues = {}) {
    switch (config.pricingType) {
      case 'per_unit':
        return `€${config.basePrice || 0} + (${value} × €${config.pricePerUnit || 0})`;

      case 'fixed':
        return `Vaste prijs: €${config.fixedPrice || 0}`;

      case 'formula':
        return `Formule: ${config.formula}`;

      case 'matrix':
        const otherValue = otherValues[config.matrixConfig?.otherField] || 0;
        const pricePerUnit = config.matrixConfig?.pricePerUnit || 0;
        const firstVal = this.parseNumericValue(value);
        const secondVal = this.parseNumericValue(otherValue);
        return `${firstVal} × ${secondVal} × €${pricePerUnit} = €${(firstVal * secondVal * pricePerUnit).toFixed(2)}`;

      default:
        return 'Onbekend';
    }
  }
}

// Export a default instance
export const pricingEngine = new PricingEngine();

// Export utility functions
export const formatPrice = (price) => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
  }).format(price);
};

export const formatCalculation = (calculation) => {
  return calculation.replace(/\*/g, '×').replace(/\//g, '÷');
};
